import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ShieldCheck, ChevronDown } from "lucide-react";
import { EASE } from "@/lib/motion";
import { PreviewTag, MetaRow } from "@/components/kit";

const Scanner = () => (
    <div className="relative h-24 w-24 shrink-0" aria-hidden="true">
        <div className="absolute inset-0 rounded-full border border-[#1677FF]/25" />
        <div className="absolute inset-2 animate-spinSlow rounded-full border border-dashed border-[#31D8FF]/40" />
        <div className="absolute inset-0 animate-scan overflow-hidden rounded-full">
            <div className="h-1/4 w-full bg-gradient-to-b from-transparent via-[#31D8FF]/10 to-transparent" />
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
            <ShieldCheck size={26} className="text-[#31D8FF]" />
        </div>
    </div>
);

export const ProofCard = ({ proof }) => {
    const [open, setOpen] = useState(false);

    return (
        <article
            data-testid={`proof-card-${proof.proofId}`}
            className="relative border border-white/[0.08] bg-[#0A1627] transition-colors duration-300 hover:border-[#1677FF]/40"
        >
            <div className="p-6 md:p-8">
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-mute">ACTION PROOF</p>
                        <h3 className="mt-2 font-mono text-xl font-semibold tracking-[0.08em] text-ink" data-testid={`proof-id-${proof.proofId}`}>
                            {proof.proofId}
                        </h3>
                    </div>
                    <div className="flex items-center gap-3">
                        <PreviewTag>SIMULATION</PreviewTag>
                        <span className="flex items-center gap-2 border border-[#31D8FF]/25 bg-[#31D8FF]/[0.06] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#31D8FF]">
                            <span className="h-1.5 w-1.5 rounded-full bg-[#31D8FF]" /> {proof.status}
                        </span>
                    </div>
                </div>

                <div className="mt-6 grid gap-x-10 gap-y-1 md:grid-cols-2">
                    <MetaRow k="TASK" v={proof.task} />
                    <MetaRow k="AGENT" v={`${proof.agent} — ${proof.agentType}`} />
                    <MetaRow k="EVIDENCE" v={`${proof.evidence.length} ITEMS ACCEPTED`} />
                    <MetaRow k="SETTLEMENT" v={proof.settlement} accent />
                </div>

                <button
                    data-testid={`proof-inspect-${proof.proofId}`}
                    onClick={() => setOpen((v) => !v)}
                    aria-expanded={open}
                    className="mt-6 inline-flex items-center gap-2.5 border border-white/15 px-5 py-3 font-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-ink transition-all duration-300 hover:border-[#1677FF]"
                >
                    {open ? "CLOSE PROOF" : "INSPECT PROOF"}
                    <ChevronDown size={13} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
                </button>
            </div>

            <AnimatePresence initial={false}>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.45, ease: EASE }}
                        className="overflow-hidden border-t border-white/[0.08] bg-[#07111F]"
                        data-testid={`proof-visualizer-${proof.proofId}`}
                    >
                        <div className="grid gap-8 p-6 md:grid-cols-[auto_1fr] md:p-8">
                            <div className="flex flex-col items-center gap-4">
                                <Scanner />
                                <p className="font-mono text-[9px] uppercase tracking-[0.24em] text-mute/70">VISUALIZATION — DEMO</p>
                            </div>
                            <div className="space-y-5">
                                <div>
                                    <p className="label-tech">ACTION</p>
                                    <p className="mt-1.5 text-sm leading-relaxed text-mute">{proof.action}</p>
                                </div>
                                <div>
                                    <p className="label-tech">EVIDENCE</p>
                                    <div className="mt-2.5 grid gap-1.5 sm:grid-cols-2">
                                        {proof.evidence.map((e) => (
                                            <span key={e.type} className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.14em] text-ink">
                                                <ShieldCheck size={12} className="text-[#31D8FF]" /> {e.type}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                                <div className="grid gap-x-8 sm:grid-cols-2">
                                    <MetaRow k="VERIFICATION" v={proof.verification} />
                                    <MetaRow k="REPUTATION" v={proof.reputation} accent />
                                </div>
                                <div>
                                    <p className="label-tech">PROOF HASH</p>
                                    <p className="mt-2 break-all font-mono text-sm tracking-[0.1em] hash-shimmer" data-testid={`proof-hash-${proof.proofId}`}>
                                        {proof.hash}
                                    </p>
                                    <p className="mt-2 font-mono text-[9px] uppercase tracking-[0.2em] text-mute/60">
                                        SIMULATED REPRESENTATION — NOT A REAL HASH
                                    </p>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </article>
    );
};

export default ProofCard;
